import React, {
    useState, useCallback, useRef, useEffect,
} from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import {
    Box, Dialog, DialogContent, IconButton,
} from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import AddIcon from '@material-ui/icons/Add';
import RemoveIcon from '@material-ui/icons/Remove';
import ButtonRound from 'components/controls/ButtonRound';
import { abreviaturas } from 'constantes';
import PdfRenderer from './PdfRenderer';
import SignaturePad from './SignaturePad';
import Camera from './Camera';
import {
    addSignatureToPdf,
    pdfBytesToBlob,
    blobToArrayBuffer,
    base64ToArrayBuffer,
    downloadPdf,
    resolveDropPosition,
    clampToPage,
} from '../../utils/pdfUtils';

const useStyles = makeStyles(() => ({
    container: {
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
    },
    pdfContainer: {
        flex: 1,
        position: 'relative',
        overflow: 'hidden',
    },
    buttonContainer: {
        position: 'fixed',
        bottom: 15,
        right: '5%',
        zIndex: 1000,
        display: 'flex',
        gap: '0.5rem',
        transform: 'scale(0.45)',
        transformOrigin: 'bottom right',
    },
    signatureOverlay: {
        position: 'absolute',
        border: '2px solid #C00333',
        backgroundColor: 'rgba(192, 3, 51, 0.1)',
        borderRadius: '4px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        pointerEvents: 'none',
        zIndex: 50,
    },
    signatureContainer: {
        position: 'absolute',
        zIndex: 50,
    },
    signatureActions: {
        position: 'absolute',
        top: -20,
        right: -16,
        display: 'flex',
        gap: 2,
        pointerEvents: 'auto',
    },
    actionButton: {
        backgroundColor: '#C00333',
        color: '#fff',
        width: 24,
        height: 24,
        padding: 2,
        '&:hover': {
            backgroundColor: '#900025',
        },
    },
    signaturePreview: {
        maxWidth: '100%',
        maxHeight: '100%',
        objectFit: 'contain',
    },
    dialogContent: {
        padding: 0,
    },
    selfiePreview: {
        position: 'fixed',
        bottom: 80,
        left: 15,
        width: '80px',
        height: '80px',
        borderRadius: '50%',
        border: '3px solid #C00333',
        objectFit: 'cover',
        zIndex: 1000,
        boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
    },
}));

function PdfViewerBase({ onHandleClick, pdfFile }) {
    const classes = useStyles();

    // Array de firmas: [{ id, position }]
    const [signatures, setSignatures] = useState([]);
    // Posicion pendiente mientras se captura la primera firma
    const [, setPendingPosition] = useState(null);
    const [signatureDataUrl, setSignatureDataUrl] = useState(null);
    const [selfieDataUrl, setSelfieDataUrl] = useState(null);
    const [showCamera, setShowCamera] = useState(false);
    const [showSignaturePad, setShowSignaturePad] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [signedPdfBlob, setSignedPdfBlob] = useState(null);
    const [isSentToServer, setIsSentToServer] = useState(false);
    const [currentPdfScale, setCurrentPdfScale] = useState(1.5);

    // Click en PDF: si ya hay firma duplica en esa posicion, si no inicia captura
    const handlePageClick = useCallback((position) => {
        const newPos = clampToPage({
            x: position.x - 75,
            y: position.y - 25,
            pageIndex: position.pageIndex,
            canvasHeight: position.canvasHeight,
            width: 150,
            height: 50,
            scale: position.scale,
        }, position.canvasWidth / position.scale, position.canvasHeight / position.scale);
        if (signatureDataUrl) {
            setSignatures((prev) => [
                ...prev,
                { id: Date.now(), position: newPos },
            ]);
        } else {
            setPendingPosition(newPos);
            setShowCamera(true);
        }
    }, [signatureDataUrl]);

    // Manejar selfie capturada
    const handleSelfieCapture = useCallback((dataUrl) => {
        setSelfieDataUrl(dataUrl);
        setShowCamera(false);
        setShowSignaturePad(true);
    }, []);

    // Omitir selfie
    const handleSkipSelfie = useCallback(() => {
        setShowCamera(false);
        setShowSignaturePad(true);
    }, []);

    // Cerrar modal de camara
    const handleCloseCamera = useCallback(() => {
        setShowCamera(false);
    }, []);

    // Firma confirmada: guarda dataUrl y crea la primera firma en pendingPosition
    const handleSignatureConfirm = useCallback((dataUrl) => {
        setSignatureDataUrl(dataUrl);
        setShowSignaturePad(false);
        setPendingPosition((pending) => {
            if (pending) {
                setSignatures([{ id: Date.now(), position: pending }]);
            }
            return null;
        });
    }, []);

    // Cerrar modal de firma
    const handleCloseSignaturePad = useCallback(() => {
        setShowSignaturePad(false);
    }, []);

    // Eliminar firma por id.
    // Si era la ultima, resetea para volver a capturar.
    const handleDeleteSignature = useCallback((id) => {
        setSignatures((prev) => {
            const remaining = prev.filter((s) => s.id !== id);
            if (remaining.length === 0) {
                setSignatureDataUrl(null);
                setSelfieDataUrl(null);
            }
            return remaining;
        });
    }, []);

    // Crecer firma individual
    const handleGrowSignature = useCallback((id) => {
        setSignatures((prev) => prev.map((s) => {
            if (s.id !== id) return s;
            return {
                ...s,
                position: {
                    ...s.position,
                    width: s.position.width + 30,
                    height: s.position.height + 10,
                },
            };
        }));
    }, []);

    // Reducir firma individual
    const handleShrinkSignature = useCallback((id) => {
        setSignatures((prev) => prev.map((s) => {
            if (s.id !== id) return s;
            return {
                ...s,
                position: {
                    ...s.position,
                    width: Math.max(s.position.width - 30, 60),
                    height: Math.max(s.position.height - 10, 20),
                },
            };
        }));
    }, []);

    // Drag: mover firma individual con mouse/touch
    const isDragging = useRef(false);
    const draggingId = useRef(null);
    const dragOffset = useRef({ x: 0, y: 0 });
    const scaleRef = useRef(currentPdfScale);
    scaleRef.current = currentPdfScale;

    const handleDragStart = useCallback((e, sigId) => {
        e.preventDefault();
        e.stopPropagation();
        isDragging.current = true;
        draggingId.current = sigId;
        const clientX = e.touches
            ? e.touches[0].clientX : e.clientX;
        const clientY = e.touches
            ? e.touches[0].clientY : e.clientY;
        setSignatures((prev) => {
            const sig = prev.find((s) => s.id === sigId);
            if (!sig) return prev;
            const { position: pos } = sig;
            const overlayLeft = pos.x * currentPdfScale;
            const pdfPageH = pos.canvasHeight / pos.scale;
            const overlayTop = pdfPageH * currentPdfScale
                - (pos.y + pos.height) * currentPdfScale;
            dragOffset.current = {
                x: clientX - overlayLeft,
                y: clientY - overlayTop,
            };
            return prev;
        });
    }, [currentPdfScale]);

    useEffect(() => {
        const handleDragMove = (e) => {
            if (!isDragging.current) return;
            e.preventDefault();
            const clientX = e.touches
                ? e.touches[0].clientX : e.clientX;
            const clientY = e.touches
                ? e.touches[0].clientY : e.clientY;
            const newPixelX = clientX - dragOffset.current.x;
            const newPixelY = clientY - dragOffset.current.y;

            setSignatures((prev) => prev.map((s) => {
                if (s.id !== draggingId.current) return s;
                const sc = scaleRef.current;
                const pdfPageH = s.position.canvasHeight
                    / s.position.scale;
                return {
                    ...s,
                    position: {
                        ...s.position,
                        x: newPixelX / sc,
                        y: pdfPageH - (newPixelY / sc)
                            - s.position.height,
                    },
                };
            }));
        };

        const handleDragEnd = () => {
            if (!isDragging.current) return;
            const sigId = draggingId.current;
            isDragging.current = false;
            draggingId.current = null;

            // Si se arrastro a otra pagina, se ancla a esa pagina
            setSignatures((prev) => prev.map((s) => (s.id === sigId
                ? { ...s, position: resolveDropPosition(s.position, scaleRef.current) }
                : s)));
        };

        window.addEventListener('mousemove', handleDragMove);
        window.addEventListener('mouseup', handleDragEnd);
        window.addEventListener(
            'touchmove', handleDragMove, { passive: false },
        );
        window.addEventListener('touchend', handleDragEnd);

        return () => {
            window.removeEventListener('mousemove', handleDragMove);
            window.removeEventListener('mouseup', handleDragEnd);
            window.removeEventListener('touchmove', handleDragMove);
            window.removeEventListener('touchend', handleDragEnd);
        };
    }, []);

    // Guardar documento firmado con todas las firmas
    const saveDocument = async () => {
        if (!signatureDataUrl || signatures.length === 0) {
            return;
        }

        setIsProcessing(true);
        try {
            let pdfBytes;
            if (pdfFile instanceof Blob) {
                pdfBytes = await blobToArrayBuffer(pdfFile);
            } else if (typeof pdfFile === 'string') {
                pdfBytes = base64ToArrayBuffer(pdfFile);
            }

            const signedPdfBytes = await addSignatureToPdf(
                pdfBytes,
                signatureDataUrl,
                signatures.map((s) => s.position),
                selfieDataUrl,
            );

            const blobFile = pdfBytesToBlob(signedPdfBytes);
            setSignedPdfBlob(blobFile);

            if (selfieDataUrl) {
                localStorage.setItem('lastSignatureSelfie', selfieDataUrl);
            }

            await onHandleClick(blobFile, selfieDataUrl);
            setIsSentToServer(true);
        } catch (error) {
            setIsProcessing(false);
        } finally {
            setIsProcessing(false);
        }
    };

    // Descargar documento firmado
    const handleDownload = useCallback(() => {
        if (signedPdfBlob) {
            downloadPdf(signedPdfBlob, 'documento_firmado');
        }
    }, [signedPdfBlob]);

    // Callback cuando cambia el zoom en PdfRenderer
    const handleScaleChange = useCallback((newScale) => {
        setCurrentPdfScale(newScale);
    }, []);

    // Construir overlays para PdfRenderer
    const signatureOverlays = signatureDataUrl
        ? signatures.map((sig) => ({
            pageIndex: sig.position.pageIndex,
            element: (
              <Box
                key={sig.id}
                className={classes.signatureContainer}
                style={{
                    left: sig.position.x * currentPdfScale,
                    top: (sig.position.canvasHeight / sig.position.scale)
                        * currentPdfScale
                        - (sig.position.y + sig.position.height)
                        * currentPdfScale,
                }}
              >
                <Box
                  className={classes.signatureOverlay}
                  style={{
                      position: 'relative',
                      width: sig.position.width * currentPdfScale,
                      height: sig.position.height * currentPdfScale,
                      cursor: 'grab',
                      pointerEvents: 'auto',
                  }}
                  onMouseDown={(e) => handleDragStart(e, sig.id)}
                  onTouchStart={(e) => handleDragStart(e, sig.id)}
                  role="presentation"
                >
                  <img
                    src={signatureDataUrl}
                    alt="Firma"
                    className={classes.signaturePreview}
                    draggable={false}
                  />
                </Box>
                <Box className={classes.signatureActions}>
                  <IconButton
                    className={classes.actionButton}
                    onClick={() => handleShrinkSignature(sig.id)}
                    size="small"
                  >
                    <RemoveIcon style={{ fontSize: 14 }} />
                  </IconButton>
                  <IconButton
                    className={classes.actionButton}
                    onClick={() => handleGrowSignature(sig.id)}
                    size="small"
                  >
                    <AddIcon style={{ fontSize: 14 }} />
                  </IconButton>
                  <IconButton
                    className={classes.actionButton}
                    onClick={() => handleDeleteSignature(sig.id)}
                    size="small"
                  >
                    <DeleteIcon style={{ fontSize: 14 }} />
                  </IconButton>
                </Box>
              </Box>
            ),
        }))
        : [];

    // Hint solo cuando no hay firma capturada aun
    const showHint = !signatureDataUrl;

    return (
      <Box className={classes.container}>
        <Box className={classes.pdfContainer}>
          <PdfRenderer
            pdfFile={pdfFile}
            onPageClick={handlePageClick}
            showHint={showHint}
            onScaleChange={handleScaleChange}
            signatureOverlays={signatureOverlays}
          />
        </Box>

        {/* Preview de selfie si existe */}
        {selfieDataUrl && (
          <img
            src={selfieDataUrl}
            alt="Selfie"
            className={classes.selfiePreview}
          />
        )}

        {/* Modal para capturar selfie */}
        <Dialog
          open={showCamera}
          onClose={handleCloseCamera}
          maxWidth="sm"
          fullWidth
        >
          <DialogContent className={classes.dialogContent}>
            <Camera
              onCapture={handleSelfieCapture}
              onSkip={handleSkipSelfie}
            />
          </DialogContent>
        </Dialog>

        {/* Modal para dibujar firma */}
        <Dialog
          open={showSignaturePad}
          onClose={handleCloseSignaturePad}
          maxWidth="sm"
          fullWidth
        >
          <DialogContent className={classes.dialogContent}>
            <SignaturePad
              onSignatureConfirm={handleSignatureConfirm}
              onCancel={handleCloseSignaturePad}
            />
          </DialogContent>
        </Dialog>

        {/* Botones de accion */}
        <div className={classes.buttonContainer}>
          {isSentToServer && signedPdfBlob && (
            <ButtonRound
              caption="Descargar"
              colorBase={abreviaturas.DEFAULT_WHITE_COLOR || 'WHITE'}
              onClickEvent={handleDownload}
            />
          )}
          {!isSentToServer && (
            <ButtonRound
              caption="Firmar"
              colorBase={abreviaturas.DEFAULT_RED_COLOR}
              onClickEvent={saveDocument}
              disabled={isProcessing || !signatureDataUrl || signatures.length === 0}
            />
          )}
        </div>
      </Box>
    );
}

PdfViewerBase.propTypes = {
    // Boton Customizado que retorna el archivo al componente padre
    onHandleClick: PropTypes.func.isRequired,
    // Debe contener el B64 del archivo a mostrar o Blob
    pdfFile: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.object,
    ]).isRequired,
};

export default PdfViewerBase;
